import { useEffect, useState } from 'react';
import { Link2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { useToast } from '@/shared/components/Toast';
import { useLinkManualProfessionalContact } from '../../hooks/useLinkManualProfessionalContact';
import type {
  ManualReferralEntitySearchItem,
  ManualReferralProfessionalRole,
} from '../../types/manual-referral.type';
import { ManualReferralEntitySelect } from './ManualReferralEntitySelect';

type LinkProvisionalContactButtonProps = {
  // The account this page belongs to — the link target.
  userId: string;
  role: ManualReferralProfessionalRole;
  accountLabel: string;
};

// Feature 101: account-anchored manual linking (design.md D6, ticket 02).
// The page IS the account, so the dialog only needs to pick an unlinked
// provisional contact of the same role — the path for contacts entered
// without an email, which signup email-match can never connect.
export const LinkProvisionalContactButton = ({
  userId,
  role,
  accountLabel,
}: LinkProvisionalContactButtonProps) => {
  const { showToast } = useToast();
  const linkContact = useLinkManualProfessionalContact();
  const [isOpen, setIsOpen] = useState(false);
  const [contact, setContact] = useState<ManualReferralEntitySearchItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setContact(null);
    setError(null);
  }, [isOpen]);

  const handleLink = async () => {
    if (!contact || contact.kind !== 'CONTACT') {
      setError('Select a provisional contact to link.');
      return;
    }

    try {
      await linkContact.mutateAsync({ contactId: contact.id, userId });
      showToast('Provisional contact linked to this account.', 'success');
      setIsOpen(false);
    } catch (linkError) {
      const message =
        linkError instanceof Error ? linkError.message : 'Unable to link the contact.';
      setError(message);
      showToast(message, 'error');
    }
  };

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setIsOpen(true)}>
        <Link2 className="mr-2 h-4 w-4" />
        Link provisional contact
      </Button>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link provisional contact</DialogTitle>
              <DialogDescription>
                Connect a referral contact who was recorded before joining the platform to{' '}
                {accountLabel}. Their referral history will follow this account.
              </DialogDescription>
            </DialogHeader>

            <ManualReferralEntitySelect
              label="Provisional contact"
              searchLabel="Provisional contact search"
              entityType={role}
              selected={contact}
              onSelect={(entity) => {
                setContact(entity);
                setError(null);
              }}
            />
            <p className="text-xs text-gray-500">
              Only results marked “Not on platform” can be linked.
            </p>
            {error && <p className="text-sm text-red-600">{error}</p>}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleLink} disabled={linkContact.isPending}>
                {linkContact.isPending ? 'Linking...' : 'Link contact'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
